import {COMPANY_KEYS} from "@/api/companyApi";
import {PAYMENT_KEYS} from "@/api/paymentApi";
import RightArrowIcon from "@/assets/colorable/right_arrow.svg?react";
import SourceContentIcon from "@/assets/colorable/source_content.svg?react";
import Button from "@/components/Common/Button";
import CheckBox from "@/components/Common/CheckBox";
import UnlockedFeaturesList from "@/components/plans/UnlockedFeaturesList";
import {useFetchUser} from "@/hooks/useFetchUser";
import {useZodState} from "@/hooks/useZodState";
import {client} from "@/httpClient";
import clsxm from "@/lib/clsxm";
import {capturePostHogEvent, PostHogEvents} from "@/posthog";
import {queryClient} from "@/queryClient";
import {useSettingsCompanyStore} from "@/store/company";
import {VAT_PERCENTAGE} from "@/store/constants";
import {useMutation} from "@tanstack/react-query";
import dayjs from "dayjs";
import {type FC} from "react";
import {useTranslation} from "react-i18next";
import {z} from "zod";

const EXTRA_BUYOUT_PRICE = 85;
const MIN_CANCEL_REASON_LENGTH = 50;

const CancelPlanSchema = z.object({
  cancelReason: z.string().min(MIN_CANCEL_REASON_LENGTH, "cancellation_reason_min_chars"),
  cancellationConfirmed: z.boolean().refine(value => value, {error: "please_complete_required_fields_to_continue"}),
});

const CancelPlanContent: FC<{onClose(): void}> = ({onClose}) => {
  const {t} = useTranslation("common");
  const company = useSettingsCompanyStore(state => state.company);
  const activePackage = company.package_subscription;
  const currency = activePackage?.currency ?? company.currency ?? "CHF";
  const vat = company.country === "Switzerland" || company.country === "Liechtenstein" ? VAT_PERCENTAGE : 0;
  const [state, setValue, validate] = useZodState(CancelPlanSchema, {
    cancelReason: "",
    cancellationConfirmed: false,
  });
  const {mutateAsync: cancelPlan, isPending: planCancellationLoading} = useMutation({
    mutationKey: PAYMENT_KEYS.mPackageSubscriptionCancel,
    mutationFn: ({cancelReason}: {cancelReason: string}) =>
      client.post(PAYMENT_KEYS.mPackageSubscriptionCancel[0], {
        cancellation_reason: cancelReason,
        success_url: window.location.href,
        cancel_url: window.location.href,
      }),
    onSuccess: async response => {
      capturePostHogEvent(PostHogEvents.SubscriptionCancelled, {
        companyId: company.id,
        packageName: activePackage?.name,
      });

      if (response.data.stripe_url) {
        window.location.href = response.data.stripe_url;
      }

      await queryClient.invalidateQueries({queryKey: COMPANY_KEYS.qCompany(company.id)});
      await refetchUser();
    },
  });
  const {refetch: refetchUser} = useFetchUser();
  const getBuyouts = () => {
    const usedBuyout = activePackage?.buyout_used;
    const yearlyBuyout = activePackage?.buyout;
    const monthlyBuyout = ~~(yearlyBuyout! / 12);
    // valid_from holds the Stripe start date the backend counts from.
    const subsStart = dayjs(activePackage?.valid_from ?? activePackage?.created);
    // Buyouts accrue until the end of the period already paid for, or until now if that is later.
    // Trialing or past-due periods are unpaid, so they accrue only until now.
    const now = dayjs();
    const isPaidPeriod = activePackage?.status === "active" && activePackage?.valid_until;
    const paidUntil = isPaidPeriod ? dayjs(activePackage.valid_until) : now;
    const accrualEnd = paidUntil.isAfter(now) ? paidUntil : now;
    // Whole elapsed 24h days, like the backend's timedelta.days; dayjs diff would shift by the DST offset.
    const daysSinceStart = Math.floor((accrualEnd.valueOf() - subsStart.valueOf()) / 86_400_000);
    const buyoutsAllowed = ~~((daysSinceStart / 365) * yearlyBuyout!);
    const overBuyout = usedBuyout! - buyoutsAllowed;

    return {monthlyBuyout, usedBuyout, yearlyBuyout, overBuyout};
  };
  const buyouts = getBuyouts();

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-2">
        <span className="text-neutral-black text-sm font-medium">{t("havingProblemWithOurPlatform")}</span>
        <Button
          buttonStyle="Blue"
          className="h-10 w-fit gap-2 px-4 text-sm font-semibold"
          onClick={() =>
            window.open(`mailto:invoice@refluenced.com?subject=${company.company_name}: ${t("cancelPlan")}`)
          }>
          {t("contactOurAdministrator")}
          <RightArrowIcon className="size-3" />
        </Button>
      </div>
      <div className="flex flex-col gap-3">
        <span className="text-neutral-black text-sm font-medium">{t("areYouSureLoseBenefits")}</span>
        <UnlockedFeaturesList
          className="gap-x-6 gap-y-2 text-sm"
          iconClassName="mr-0 size-4"
          subscription={activePackage}
        />
        {buyouts.overBuyout > 0 && activePackage?.package_type === "MONTHLY" && (
          <div className="border-neutral-grey-light-dark text-neutral-grey-dark rounded-lg border px-3 py-2 text-sm">
            {t("cancellationBuyoutAlert", {
              monthlyBuyout: buyouts.monthlyBuyout,
              yearlyBuyout: buyouts.yearlyBuyout,
              usedBuyout: buyouts.usedBuyout,
              overBuyout: buyouts.overBuyout,
            })}
          </div>
        )}
        <div className="border-neutral-grey-light-dark text-neutral-grey-dark rounded-lg border px-3 py-2 text-sm">
          {t("comeBackAnytime")}
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <CheckBox
            className={clsxm("p-1", state.errors.cancellationConfirmed && "border-red-500")}
            isChecked={state.cancellationConfirmed}
            onChange={newValue => setValue("cancellationConfirmed", newValue)}
          />
          <span
            className={clsxm(
              "text-neutral-black text-sm font-medium",
              state.errors.cancellationConfirmed && "text-red-500",
            )}>
            {activePackage?.status === "trialing"
              ? t("confirmCancellationTrialing")
              : t("confirmCancellation", {
                  date: dayjs(activePackage?.valid_until).format("DD.MM.YYYY"),
                })}
          </span>
        </div>
        {state.errors.cancellationConfirmed && (
          <span className="text-xs font-medium text-red-500">
            {t(state.errors.cancellationConfirmed as "please_complete_required_fields_to_continue")}
          </span>
        )}
        {activePackage?.status === "trialing" && state.cancellationConfirmed && (
          <span className="text-xs font-semibold text-red-500">{t("planCancellationWarningInTrial")}</span>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <span className="text-neutral-black text-sm font-medium">
          {t("cancellation_reason_question")}{" "}
          {state.cancelReason.length < MIN_CANCEL_REASON_LENGTH && (
            <span className={clsxm("text-xs", state.errors.cancelReason ? "text-red-500" : "text-neutral-grey-dark")}>
              {t("cancellation_reason_min_chars", {
                count: state.cancelReason.length,
                limit: MIN_CANCEL_REASON_LENGTH,
              })}
            </span>
          )}
        </span>
        <textarea
          className={clsxm(
            "h-24 w-full rounded-lg border p-3 text-sm outline-none",
            state.errors.cancelReason
              ? "border-red-500 focus:border-red-500"
              : "border-neutral-grey-light-dark focus:border-primary-medium-solid",
          )}
          placeholder={t("shortReview")}
          minLength={MIN_CANCEL_REASON_LENGTH}
          onChange={e => setValue("cancelReason", e.target.value)}
          value={state.cancelReason}
        />
      </div>
      {buyouts.overBuyout > 0 && activePackage?.package_type === "MONTHLY" && (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <span className="text-neutral-black text-sm font-medium">{t("overContentBuyouts")}</span>
            <div className="border-neutral-grey-light-dark flex items-center gap-2 rounded-lg border px-3 py-2">
              <SourceContentIcon className="text-neutral-black size-3" />
              <span className="text-sm font-medium">{buyouts.overBuyout}</span>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <span className="text-neutral-black text-sm font-medium">{t("priceToPay")}</span>
            <span className="text-2xl font-semibold">
              {currency} {((buyouts.overBuyout * EXTRA_BUYOUT_PRICE * (1 + vat / 100)) as number).toFixed(2)}
            </span>
          </div>
        </div>
      )}
      <div className="flex gap-3">
        <Button
          buttonStyle="White"
          className="h-10 flex-1 px-5 text-sm sm:flex-none"
          isDisabled={planCancellationLoading}
          onClick={onClose}>
          {t("cancel")}
        </Button>
        <Button
          buttonStyle="Danger"
          className="h-10 flex-1 px-5 text-sm sm:flex-none"
          isDisabled={planCancellationLoading}
          isLoading={planCancellationLoading}
          onClick={async () => {
            if (!validate()) return;
            await cancelPlan({cancelReason: state.cancelReason});
            onClose();
          }}>
          {t("confirm")}
        </Button>
      </div>
    </div>
  );
};

export default CancelPlanContent;
